'use client';

import { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import type { Activity } from '@/lib/api/types';
interface CommentItemProps {
  comment: Activity;
  currentUserId?: string;
  onDelete: (activityId: string) => Promise<void>;
}
export function CommentItem({
  comment,
  currentUserId,
  onDelete
}: CommentItemProps) {
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const isOwner = currentUserId && String(comment.actorId) === String(currentUserId);
  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await onDelete(comment.id);
      setShowDeleteDialog(false);
    } catch (error) {
      console.error('Failed to delete comment:', error);
    } finally {
      setIsDeleting(false);
    }
  };
  const relativeTime = formatDistanceToNow(new Date(comment.createdAt), {
    addSuffix: true
  });

  // Get user display name
  const userName = comment.user?.name || `User ${comment.actorId}`;

  // Get user initials for avatar
  const getInitials = (name: string) => {
    if (name.startsWith('User ')) {
      return comment.actorId.toString().slice(0, 2).toUpperCase();
    }
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };
  return <>
      <div className="flex gap-3 p-4 rounded-lg hover:bg-gray-50 transition-colors">
        <div className="flex-shrink-0">
          {comment.user?.image ? <img src={comment.user.image} alt={userName} className="w-10 h-10 rounded-full object-cover" /> : <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
              <span className="text-sm font-medium text-primary-700">{getInitials(userName)}</span>
            </div>}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm font-medium text-gray-900">{userName}</span>
            <span className="text-xs text-gray-500">{relativeTime}</span>
          </div>

          <p className="text-sm text-gray-700 whitespace-pre-wrap break-words">{comment.content}</p>
        </div>

        {isOwner && <div className="flex-shrink-0">
            <Button variant="ghost" size="sm" onClick={() => setShowDeleteDialog(true)} disabled={isDeleting} className="text-gray-400 hover:text-red-600 h-8 w-8 p-0">
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>}
      </div>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Comment</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this comment? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={isDeleting}>
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>;
}